import { type FrameVariant } from '@packages/surveys-widget-common/src/api'
import { constant, usePxIfNotString } from '@packages/surveys-widget-common/src'
import './widget.css'

type MobileGlobalCss =
  | {
      action: 'full-screen'
    }
  | {
      action: 'exact-height'
      wrapperHeight: number | string
    }

export class WidgetDOM {
  wrapper: HTMLDivElement
  iframe: HTMLIFrameElement
  variant?: FrameVariant
  visible: boolean
  height: number
  private isTouched
  private resizeListeners: Array<() => void>
  private loadListeners: Array<() => void>

  constructor({
    visible,
    file = 'core.html',
  }: { visible?: boolean; file?: string } = {}) {
    this.visible = visible || false
    this.isTouched = false
    this.resizeListeners = []
    this.loadListeners = []
    this.height = 200

    this.updateDimensions = this.updateDimensions.bind(this)
    this.iframeLoaded = this.iframeLoaded.bind(this)

    this.wrapper = this.createWrapper()
    this.iframe = this.createIframe(file)

    this.wrapper.appendChild(this.iframe)

    this.updateDimensions()
    this.addEventListeners()
    this.updateIframeColorScheme()

    document.body.appendChild(this.wrapper)
  }

  private createWrapper(): HTMLDivElement {
    const wrapper = document.createElement('div')
    wrapper.className =
      'sp-fixed sp-overflow-hidden sp-right-0 sp-bottom-0 sp-pointer-events-none'
    wrapper.style.zIndex = '2147483647'
    wrapper.style.maxWidth = 'none'
    wrapper.style.visibility = 'hidden'
    return wrapper
  }

  private iframeLoaded() {
    if (this.visible) {
      this.wrapper.style.visibility = 'visible'
    }

    this.loadListeners.forEach(listener => listener())
  }

  getColorScheme = () => {
    if (!window.matchMedia) {
      return 'light'
    }

    if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark'
    } else if (window.matchMedia('(prefers-color-scheme: light)').matches) {
      return 'light'
    } else {
      return 'no-preference'
    }
  }

  public updateIframeColorScheme = () => {
    const colorScheme = this.getColorScheme()
    this.iframe.style.colorScheme = colorScheme === 'dark' ? 'dark' : 'light'
  }

  private createIframe(file: string): HTMLIFrameElement {
    const iframe = document.createElement('iframe')
    iframe.src = `${process.env.RUNTIME_FRAME_URL}/${file}`
    iframe.className = 'sp-w-full sp-h-full sp-absolute sp-bg-transparent'
    iframe.style.pointerEvents = 'all'
    iframe.style.colorScheme = 'light'
    iframe.setAttribute('allowTransparency', 'true')
    iframe.addEventListener('load', this.iframeLoaded)

    return iframe
  }

  setWrapperHeight(opts: MobileGlobalCss) {
    if (opts.action === 'full-screen') {
      this.wrapper.style.height = '100%'
      this.wrapper.style.minHeight = '100%'
      this.wrapper.style.setProperty('left', '0')
      this.wrapper.style.setProperty('right', '0')
      document.documentElement.classList.add('sp-mobile-widget-active')
    } else {
      this.wrapper.style.height = usePxIfNotString(opts.wrapperHeight)
      this.wrapper.style.removeProperty('left')
      this.wrapper.style.removeProperty('right')
      this.wrapper.style.removeProperty('minHeight')
      document.documentElement.classList.remove('sp-mobile-widget-active')
    }
  }

  public updateDimensions() {
    const d = this.dimensions(this.variant || 'classic')
    this.wrapper.style.width = usePxIfNotString(d.width)

    if (d.height) {
      this.setWrapperHeight({
        action: 'exact-height',
        wrapperHeight: d.height,
      })
    } else {
      this.setWrapperHeight({
        action: 'full-screen',
      })
    }

    this.resizeListeners.forEach(listener => listener())
  }

  private dimensions(variant: FrameVariant) {
    switch (variant) {
      case 'avatar': {
        if (window.innerWidth >= constant.mediumDeviceWidth) {
          return {
            width: constant.widgetSizes.withAvatar.mediumDevice.width,
            height: this.height,
          }
        }

        if (window.innerWidth >= constant.smallDeviceWidth) {
          return {
            width: constant.widgetSizes.withAvatar.smallDevice.width,
            height: this.height,
          }
        }

        if (this.isTouched) {
          return {
            width: constant.widgetSizes.withAvatar.default.width,
          }
        }

        return {
          width: constant.widgetSizes.withAvatar.default.width,
          height: this.height,
        }
      }

      case 'classic': {
        if (window.innerWidth >= constant.mediumDeviceWidth) {
          return {
            width: constant.widgetSizes.classic.mediumDevice.width,
            height: this.height,
          }
        }

        if (window.innerWidth >= constant.smallDeviceWidth) {
          return {
            width: constant.widgetSizes.classic.smallDevice.width,
            height: this.height,
          }
        }

        if (this.isTouched) {
          return {
            width: constant.widgetSizes.classic.default.width,
          }
        }

        return {
          width: constant.widgetSizes.classic.default.width,
          height: this.height,
        }
      }
    }
  }

  private addEventListeners() {
    window.addEventListener('resize', this.updateDimensions)
    window.addEventListener('orientationchange', this.updateDimensions)
  }

  public destroy() {
    window.removeEventListener('resize', this.updateDimensions)
    window.removeEventListener('orientationchange', this.updateDimensions)
    this.iframe.removeEventListener('load', this.iframeLoaded)
    this.resizeListeners = []
    this.loadListeners = []

    // hack for removing global styles
    this.setWrapperHeight({
      action: 'exact-height',
      wrapperHeight: 0,
    })
  }

  public updateVariant(variant: FrameVariant) {
    this.variant = variant
    this.updateDimensions()
  }

  public setTouched() {
    this.isTouched = true
    this.updateDimensions()
  }

  public updateVisibility(visible: boolean) {
    this.visible = visible
    this.wrapper.style.visibility = visible ? 'visible' : 'hidden'
  }

  public updateHeight(height: number) {
    if (this.height !== height) {
      this.height = height
      this.updateDimensions()
    }
  }

  public registerResizeListener(listener: () => void) {
    this.resizeListeners.push(listener)
  }

  public registerLoadListener(listener: () => void) {
    this.loadListeners.push(listener)
  }
}
